import L from 'leaflet'

export interface WeatherArrowOptions extends L.LayerOptions {
  size?: number
  direction?: number
  color?: string
  stroke?: boolean
  weight?: number
  fill?: boolean
  fillColor?: string
  fillOpacity?: number
  item: {
    speed: any
    direction: any
    direction_old: any
  }
}

class WeatherArrow extends L.Path {
  private _latlng: L.LatLng
  private _size: number
  private _direction: number
  private _arrowSymbol: number[]

  constructor(
    latlng: L.LatLngLiteral | L.LatLng,
    options?: WeatherArrowOptions
  ) {
    super(options)

    if (latlng === undefined) {
      throw new Error('Please give a valid lat/lon-position')
    }

    L.setOptions(this, options || {})

    // this._leaflet_id = this._id
    this._latlng = L.latLng(latlng)
    this._size = options?.size || 16
    this._direction = ((options?.direction || 0) * Math.PI) / 180
    const size = this._size / 2
    this._arrowSymbol = [
      -size,
      0,
      size - 6,
      4,
      -(size - 5),
      1,
      size,
      1,
      size,
      -1,
      -(size - 5),
      -1,
      size - 6,
      -4,
    ]

    this.setStroke(options?.stroke ?? true)
    this.setColor(options?.color ?? '#ff0000')
    this.setWeight(options?.weight ?? 0.5)
    this.setFill(options?.fill ?? true)
    this.setFillOpacity(options?.fillOpacity ?? 1)
    this.setFillColor(options?.color ?? '#ff0000')
  }

  setColor(color: string): this {
    this.setStyle({ color })
    return this.redraw()
  }

  setFillOpacity(fillOpacity: number): this {
    this.setStyle({ fillOpacity })
    return this.redraw()
  }

  setOpacity(opacity: number): this {
    this.setStyle({ opacity })
    return this.redraw()
  }

  setWeight(weight: number): this {
    this.setStyle({ weight })
    return this.redraw()
  }

  setFill(fill: boolean): this {
    this.setStyle({ fill })
    return this.redraw()
  }

  setStroke(stroke: boolean): this {
    this.setStyle({ stroke })
    return this.redraw()
  }

  setFillColor(fillColor: string): this {
    this.setStyle({ fillColor })
    return this
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _project(): void {}

  protected _update(): void {
    this._setPath()
  }

  protected _setPath(): void {
    const _ = this as any
    _._path.setAttribute('d', this.getPathString())
  }

  setLatLng(latlng: L.LatLngLiteral | L.LatLng): this {
    const oldLatLng = this._latlng
    this._latlng = L.latLng(latlng)
    this.fire('move', { oldLatLng, latlng })
    return this.redraw()
  }

  setDirection(direction: number): this {
    this._direction = direction
    return this.redraw()
  }

  // @ts-ignore: TS6133
  private _getLatSizeOf(size: number): number {
    return (size / 40075017) * 360
  }
  // @ts-ignore: TS6133
  private _getLngSizeOf(size: number): number {
    return (
      ((size / 40075017) * 360) / Math.cos((Math.PI / 180) * this._latlng.lat)
    )
  }

  getLatLng(): L.LatLng {
    return this._latlng
  }

  private _rotate(point: [number, number], angle: number): [number, number] {
    const [x, y] = point
    const sin = Math.sin(angle)
    const cos = Math.cos(angle)
    return [x * cos - y * sin, x * sin + y * cos]
  }

  private _rotateAllPoints(points: number[], angle: number): number[] {
    const rotatedPoints: number[] = []
    for (let i = 0; i < points.length; i += 2) {
      const x = points[i]
      const y = points[i + 1]
      const rotated = this._rotate([x, y], angle)
      rotatedPoints.push(rotated[0], rotated[1])
    }
    return rotatedPoints
  }

  private _transformAllPointsToView(points: number[]): number[] {
    const viewPoints: number[] = []
    const layerPoint = this._map.latLngToLayerPoint(this._latlng)
    for (let i = 0; i < points.length; i += 2) {
      const x = layerPoint.x + points[i]
      const y = layerPoint.y - points[i + 1]
      viewPoints.push(x, y)
    }
    return viewPoints
  }

  private _createPathFromPoints(points: number[]): string {
    let pathString = ''
    for (let i = 0; i < points.length; i += 2) {
      const x = points[i]
      const y = points[i + 1]
      if (!pathString) {
        pathString = `M ${x} ${y} `
      } else {
        pathString += `L ${x} ${y} `
      }
    }
    return `${pathString} Z`
  }

  private _createViewAngleFromModel(direction: number): number {
    return Math.PI / 2 - direction
  }

  // private _resizeAndMovePoint(
  //   point: [number, number],
  //   scale: [number, number],
  //   offset: [number, number]
  // ): [number, number] {
  //   return [point[0] * scale[0] + offset[0], point[1] * scale[1] + offset[1]]
  // }

  getPathString(): string {
    const viewAngle = this._createViewAngleFromModel(this._direction)
    const transformedPoints = this._transformAllPointsToView(
      this._rotateAllPoints(this._arrowSymbol, viewAngle)
    )
    return this._createPathFromPoints(transformedPoints)
  }
}

// 创建工厂方法
const weatherArrow = (
  latlng: L.LatLngLiteral | L.LatLng,
  options?: WeatherArrowOptions
) => new WeatherArrow(latlng, options)

export { weatherArrow }
